/* Force deploy JS v13 — debounced batching for mobile perf */
/**
 * PrepackForm Custom Element
 * Handles the UI and logic for adding/removing B2B prepacks using the Shopify AJAX Cart API.
 * Uses event delegation internally and optimistic UI rendering for high perceived performance.
 * 
 * Features:
 * - Initializes state by reading current cart contents from /cart.js on load
 * - Submits deltas (additions/removals) using POST /cart/update.js to avoid cart corruption
 * - Updates the cart drawer/header natively by requesting specific sections
 * - Tags all added variants with the `_prepack_id` line item property to track groupings over to checkout
 */

/**
 * Helper to fetch with built-in retries, mostly used to defeat transient
 * HTML caching layers or network blips when extracting inline JSON maps.
 */
async function fetchWithRetry(url, options, retries = 1) {
  try {
    const res = await fetch(url, options);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res;
  } catch (err) {
    if (retries > 0) return fetchWithRetry(url, options, retries - 1);
    throw err;
  }
}

/**
 * Calculates how many complete packs can fit into the provided virtual pantry 
 * using a "fill with rest 0" conceptual loop. Modifies the provided pantry object in-place.
 */
function getFillCount(dist, virtualPantry) {
  let hasTrackedVariants = false;
  for (const d of dist) {
    const v = virtualPantry[d.variant_id];
    if (v && v.management === 'shopify' && v.policy === 'deny') {
      const required = parseInt(d.qty, 10);
      if (required > 0) {
        hasTrackedVariants = true;
        break;
      }
    }
  }
  
  if (!hasTrackedVariants) return 9999;

  let count = 0;
  while(count < 10000) { // Safety limit to prevent absolute infinite loops
    let canFit = true;
    for (const d of dist) {
      const v = virtualPantry[d.variant_id];
      if (v && v.management === 'shopify' && v.policy === 'deny') {
        const required = parseInt(d.qty, 10);
        if (required <= 0) continue;
        
        // Clamp negative inventory to 0 conceptually
        const available = Math.max(0, v.qty);
        if (available < required) {
          canFit = false;
          break;
        }
      }
    }
    
    if (!canFit) break;
    
    // Fit 1 pack, drain the buckets
    for (const d of dist) {
      const v = virtualPantry[d.variant_id];
      if (v && v.management === 'shopify' && v.policy === 'deny') {
        const required = parseInt(d.qty, 10);
        if (required > 0) {
          v.qty -= required;
        }
      }
    }
    count++;
  }
  return count;
}

/**
 * Solves the linear system A * x = b using least squares Gaussian elimination.
 * Reconstructs exactly how many packs were sold based on the consumed variant inventory.
 */
function solvePacksSold(packs, consumed) {
  const n = packs.length;
  if (n === 0) return [];

  const variantIds = Object.keys(consumed);
  const A = variantIds.map(vid =>
    packs.map(p => {
      const d = p.dist.find(d => String(d.variant_id) === vid);
      return d ? parseInt(d.qty, 10) : 0;
    })
  );
  const b = variantIds.map(vid => consumed[vid] || 0);

  const At = packs.map((_, j) => variantIds.map((_, i) => A[i][j]));
  const AtA = At.map(row => At.map(col => row.reduce((s, _, k) => s + row[k] * col[k], 0)));
  const Atb = At.map(row => row.reduce((s, v, i) => s + v * b[i], 0));

  const M = AtA.map((row, i) => [...row, Atb[i]]);
  for (let col = 0; col < n; col++) {
    let pivot = col;
    for (let row = col + 1; row < n; row++) {
      if (Math.abs(M[row][col]) > Math.abs(M[pivot][col])) pivot = row;
    }
    [M[col], M[pivot]] = [M[pivot], M[col]];
    const d = M[col][col];
    if (Math.abs(d) < 1e-10) continue;
    for (let row = 0; row < n; row++) {
      if (row === col) continue;
      const factor = M[row][col] / d;
      for (let k = col; k <= n; k++) M[row][k] -= factor * M[col][k];
    }
  }
  
  return M.map((row, i) => {
    const val = row[i] !== 0 ? Math.round(row[n] / row[i]) : 0;
    return Math.max(0, val);
  });
}

class PrepackForm extends HTMLElement {
  constructor() {
    super();
    this.syncLocks = new Set();
    this.cart = null;
  }

  connectedCallback() {

    // Defer initialization to ensure innerHTML is fully parsed and Dawn has finished script replacements
    setTimeout(() => {
      this.productId = this.dataset.productId;
      this.productTitle = this.dataset.productTitle;
      this.moneyFormat = this.dataset.moneyFormat || '€{{amount}}';
      
      this.rows = this.querySelectorAll('.prepack-b2b-row');

      
      this.errorMsg = this.querySelector('.prepack-error-message');
      
      // Target elements locally to support multiple modals on the same page
      this.globalQtyEl = this.querySelector('.global-total-qty');
      this.globalPriceEl = this.querySelector('.global-total-price');
      
      this.init();
    }, 100);
  }



  async init() {

    // Determine existing cart state to initialize input values
    try {
      const res = await fetch(`${window.Shopify.routes.root}cart.js`);
      this.cart = await res.json();
      const cart = this.cart;
      
      this.rows.forEach(row => {
        const packId = row.dataset.packId;
        const scriptTag = row.querySelector('.pack-distribution-data');
        if(!scriptTag) return;
        
        const distribution = JSON.parse(scriptTag.textContent);
        const input = row.querySelector('.prepack-qty-input');
        
        // Count how many complete packs are in the cart for this prepack_id
        const itemsInCartForPack = cart.items.filter(item => 
          item.properties && item.properties._prepack_id === `${this.productId}:${packId}`
        );
        
        let minPackQty = 0;
        if (itemsInCartForPack.length > 0 && distribution.length > 0) {
          const distItem = distribution[0];
          const cartItem = itemsInCartForPack.find(item => item.variant_id == distItem.variant_id);
          if (cartItem) {
            minPackQty = Math.floor(cartItem.quantity / distItem.qty);
          }
        }
        
        row.dataset.currentCartQty = minPackQty;
        input.value = minPackQty;
      });
      
      this.updateTotals();
      
      const mapScript = this.querySelector(`#prepack-inventory-map-${this.productId}`) || document.getElementById(`prepack-inventory-map-${this.productId}`);
      if (mapScript) {
        this.baseInventoryMap = JSON.parse(mapScript.textContent);

      } else {
        console.error('[Prepack] CRITICAL FAILURE: baseInventoryMap NOT FOUND. System has no initial state.');
        window.Sentry?.captureMessage('[Prepack] Initial map missing', { level: 'fatal' });
        // Fatal page-load error: disable all prepack UI
        this.rows.forEach(r => {
          const input = r.querySelector('.prepack-qty-input');
          const btnPlus = r.querySelector('.prepack-btn-plus');
          const btnMinus = r.querySelector('.prepack-btn-minus');
          if (input) input.disabled = true;
          if (btnPlus) btnPlus.disabled = true;
          if (btnMinus) btnMinus.disabled = true;
        });
        return; // Stop initialization
      }
      this._recomputeCaps();
      
      this.bindEvents();
    } catch(e) {
      console.error('[PrepackForm] Failed to init prepack form state', e);
      // fallback
      this.rows.forEach(r => {
        r.dataset.currentCartQty = 0; 
        r.querySelector('.prepack-qty-input').value = 0;
      });
      this.updateTotals();
      this.bindEvents();
    }
    this._fetchLiveInventoryAndSolve();
  }

  async _fetchLiveInventoryAndSolve() {
    try {
      if (!this.baseInventoryMap) return;

      const currentInv = {};
      Object.keys(this.baseInventoryMap).forEach(vid => {
        currentInv[vid] = Math.max(0, this.baseInventoryMap[vid].qty || 0);
      });

      const packs = [];
      this.rows.forEach(row => {
        const scriptTag = row.querySelector('.pack-distribution-data');
        if (!scriptTag) return;
        const dist = JSON.parse(scriptTag.textContent);
        let packStockRaw = row.dataset.initialStock;
        if (!packStockRaw) {
          packStockRaw = row.dataset.packStock;
          row.dataset.initialStock = packStockRaw; // Lock initial value to prevent corruption
        }
        const initial_stock = (!packStockRaw || packStockRaw === 'null') ? 0 : parseInt(packStockRaw, 10);
        packs.push({ row, dist, initial_stock });
      });

      if (packs.length === 0) return;

      const initialInv = {};
      packs.forEach(pack => {
        pack.dist.forEach(d => {
          initialInv[d.variant_id] = (initialInv[d.variant_id] || 0) + (pack.initial_stock * parseInt(d.qty, 10));
        });
      });

      const consumed = {};
      Object.keys(initialInv).forEach(vid => {
        consumed[vid] = Math.max(0, (initialInv[vid] || 0) - (currentInv[vid] || 0));
      });

      const packsSold = solvePacksSold(packs, consumed);

      // Validate residual (Risk #4: Least-squares hides inconsistency)
      let inconsistent = false;
      Object.keys(initialInv).forEach(vid => {
        let calculatedConsumption = 0;
        packs.forEach((p, i) => {
          const d = p.dist.find(x => String(x.variant_id) === vid);
          if (d) calculatedConsumption += (parseInt(d.qty, 10) * (packsSold[i] || 0));
        });
        if (calculatedConsumption !== consumed[vid]) {
          inconsistent = true;
        }
      });

      if (inconsistent) {
        console.warn('[Prepack] Inconsistent variant inventory detected. Falling back to loose variant limits.');
        window.Sentry?.captureMessage('[Prepack] Inconsistent variant inventory', { level: 'warning' });
        window.dispatchEvent(new CustomEvent('prepack:error', { detail: 'Inconsistent variant inventory' }));
        // Do not overwrite packStock, let getFillCount handle the actual availability safely
      } else {
        packs.forEach((pack, i) => {
          // If system is completely disjointed, packsSold[i] might be undefined, fallback to 0
          const sold = packsSold[i] || 0;
          const remainingStock = Math.max(0, pack.initial_stock - sold);
          pack.row.dataset.packStock = remainingStock;
        });
      }


      this._recomputeCaps();
    } catch (e) {
      console.error('[PrepackForm] Failed to solve live inventory:', e);
    }
  }

  _recomputeCaps() {
    if (!this.baseInventoryMap) return;
    
    // Clone base map to virtual pantry
    const virtualPantry = JSON.parse(JSON.stringify(this.baseInventoryMap));
    
    // Subtract standard items currently in the cart
    if (this.cart && this.cart.items) {
      this.cart.items.forEach(item => {
        if (item.product_id == this.productId) {
          if (item.properties && item.properties._prepack_id && item.properties._prepack_id.startsWith(`${this.productId}:`)) return;
          if (virtualPantry[item.variant_id]) {
            virtualPantry[item.variant_id].qty -= item.quantity;
          }
        }
      });
    }
    
    // Subtract current input selections across all rows to get the true remaining "pantry"
    const selections = [];
    this.rows.forEach(row => {
      const input = row.querySelector('.prepack-qty-input');
      const qty = parseInt(input.value, 10) || 0;
      const scriptTag = row.querySelector('.pack-distribution-data');
      if (!scriptTag) return;
      const dist = JSON.parse(scriptTag.textContent);
      selections.push({ row, qty, dist });
      
      dist.forEach(d => {
        if (virtualPantry[d.variant_id]) {
          virtualPantry[d.variant_id].qty -= (parseInt(d.qty, 10) * qty);
        }
      });
    });
    
    // Calculate remaining capacity for each row
    selections.forEach(({ row, qty, dist }) => {
      // Create an isolated clone of the remaining pantry for this specific row's calculation
      const pantryForFill = JSON.parse(JSON.stringify(virtualPantry));
      
      const availableToAdd = getFillCount(dist, pantryForFill);
      const variantCap = qty + availableToAdd;
      
      const packStockRaw = row.dataset.packStock;
      const packStock = (!packStockRaw || packStockRaw === 'null') ? Infinity : parseInt(packStockRaw, 10);
      
      const absoluteMax = Math.min(packStock, variantCap);
      const input = row.querySelector('.prepack-qty-input');
      
      row.dataset.maxPacks = absoluteMax;
      input.setAttribute('max', absoluteMax);
      
      const label = row.querySelector('.prepack-available-label');
      if (label) {
        if (absoluteMax === 9999 || absoluteMax === Infinity) {
          label.innerHTML = '';
        } else if (absoluteMax === 0) {
          label.innerHTML = '<strong class="prepack-low-stock-badge">(Sold Out)</strong>';
        } else {
          label.innerHTML = `(Available: <strong>${absoluteMax}</strong>)`;
        }
      }
      
      if (absoluteMax === 0 && qty === 0) {
        row.classList.add('prepack-b2b-row--sold-out');
      } else {
        row.classList.remove('prepack-b2b-row--sold-out');
      }
      
      const btnPlus = row.querySelector('.btn-plus');
      if (btnPlus) {
        btnPlus.disabled = (qty >= absoluteMax);

      }
    });
  }

  async _refreshInventory() {
    try {
      const nocacheUrl = `${window.location.pathname}?t=${Date.now()}`;
      const res = await fetchWithRetry(nocacheUrl, { cache: 'no-store' });
      const text = await res.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, 'text/html');
      const mapScript = doc.querySelector(`#prepack-inventory-map-${this.productId}`);
      
      if (!mapScript) {
        console.error(`[Prepack] CRITICAL FAILURE: Could not find script tag #prepack-inventory-map-${this.productId}. The matrix solver cannot refresh and is now using stale inventory data.`);
        window.Sentry?.captureMessage('[Prepack] Product refresh selector missed', { level: 'error' });
        window.dispatchEvent(new CustomEvent('prepack:error', { detail: 'Product refresh selector missed' }));
        return;
      }
      
      this.baseInventoryMap = JSON.parse(mapScript.textContent);
      await this._fetchLiveInventoryAndSolve();
    } catch(e) { 
      console.error('[Prepack] Failed to refresh inventory', e); 
      window.Sentry?.captureMessage('[Prepack] Failed to refresh inventory', { level: 'error' });
    }
  }

  bindEvents() {
    // Per-row debounce timers for batched sync
    this._debounceTimers = new Map();

    this.rows.forEach(row => {
      const input = row.querySelector('.prepack-qty-input');
      const btnMinus = row.querySelector('.btn-minus');
      const btnPlus = row.querySelector('.btn-plus');
      const btnTrash = row.querySelector('.btn-qty-trash');

      const scheduleSync = (newVal) => {
        const packId = row.dataset.packId;
        // If a network request is already in flight for this row, reject new taps
        if (this.syncLocks.has(packId)) return;

        let max = parseInt(input.getAttribute('max'), 10) || 9999;
        if (isNaN(newVal) || newVal < 0) newVal = 0;
        if (newVal > max) newVal = max;

        const confirmedCartQty = parseInt(row.dataset.currentCartQty, 10) || 0;

        // Update UI immediately (optimistic) — no lock, no opacity change
        input.value = newVal;
        this.updateTotals();
        this._recomputeCaps();

        // If we're back to the confirmed qty, cancel any pending sync
        if (newVal === confirmedCartQty) {
          clearTimeout(this._debounceTimers.get(packId));
          this._debounceTimers.delete(packId);
          row.classList.remove('prepack-b2b-row--syncing');
          return;
        }

        // Show syncing indicator (pulsing dots)
        row.classList.add('prepack-b2b-row--syncing');

        // Debounce: reset timer on every tap, fire after 600ms of silence
        clearTimeout(this._debounceTimers.get(packId));
        this._debounceTimers.set(packId, setTimeout(async () => {
          this._debounceTimers.delete(packId);
          const finalVal = parseInt(input.value, 10) || 0;
          const oldVal = parseInt(row.dataset.currentCartQty, 10) || 0;
          if (finalVal === oldVal) {
            row.classList.remove('prepack-b2b-row--syncing');
            return;
          }

          // Lock row during network request
          this.syncLocks.add(packId);
          input.disabled = true;
          if (btnMinus) btnMinus.disabled = true;
          if (btnPlus) btnPlus.disabled = true;

          await this.syncDelta(row, oldVal, finalVal);

          // Unlock
          input.disabled = false;
          if (btnMinus) btnMinus.disabled = false;
          if (btnPlus) btnPlus.disabled = false;
          row.classList.remove('prepack-b2b-row--syncing');
          this._recomputeCaps();
        }, 600));
      };

      if (btnMinus) {
        btnMinus.addEventListener('click', () => {
          let val = parseInt(input.value, 10) || 0;
          if (val > 0) scheduleSync(val - 1);
        });
      }

      if (btnPlus) {
        btnPlus.addEventListener('click', () => {
          let val = parseInt(input.value, 10) || 0;
          let max = parseInt(input.getAttribute('max'), 10) || 9999;
          if (val < max) scheduleSync(val + 1);
        });
      }

      if (btnTrash) {
        btnTrash.addEventListener('click', () => {
          scheduleSync(0);
        });
      }

      if (input) {
        input.addEventListener('change', () => {
          let val = parseInt(input.value, 10);
          scheduleSync(val);
        });
      }
    });

    // Global Remove All handler
    const btnRemoveAll = this.querySelector('.prepack-remove-all');
    if (btnRemoveAll) {
      btnRemoveAll.addEventListener('click', async () => {
        if(this.syncLocks.has('remove-all')) return;
        this.syncLocks.add('remove-all');
        this.errorMsg.classList.add('hidden');
        
        // Cancel any pending debounce timers
        this._debounceTimers.forEach(t => clearTimeout(t));
        this._debounceTimers.clear();

        // Optimistic UI
        this.rows.forEach(row => {
           row.querySelector('.prepack-qty-input').value = 0;
           row.style.opacity = '0.6';
           row.querySelector('.prepack-qty-input').disabled = true;
        });
        
        try {
           if (!this.cart) {
             const res = await fetch(`${window.Shopify.routes.root}cart.js`);
             this.cart = await res.json();
           }
           const cart = this.cart;
           
           const updates = {};
           cart.items.forEach(item => {
             if (item.properties && item.properties._prepack_id && item.properties._prepack_id.startsWith(`${this.productId}:`)) {
               updates[item.key] = 0;
             }
           });
           
           if(Object.keys(updates).length > 0) {
              const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify({ 
                  updates,
                  sections: ['cart-icon-bubble'],
                  sections_url: window.location.pathname
                })
              });
              const data = await response.json();
              this.cart = data;
              
              if (data.sections && data.sections['cart-icon-bubble']) {
                const oldBubble = document.getElementById('cart-icon-bubble');
                const tempDiv = new DOMParser().parseFromString(data.sections['cart-icon-bubble'], 'text/html');
                const newBubble = tempDiv.getElementById('cart-icon-bubble') || tempDiv.body.firstElementChild;
                if (oldBubble && newBubble) oldBubble.innerHTML = newBubble.innerHTML;
              }
           }
           
           this.rows.forEach(row => {
             row.dataset.currentCartQty = 0;
             row.querySelector('.prepack-qty-input').value = 0;
           });
           
           document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
           window.location.reload();
           
        } catch(e) {
           console.error('Error removing all prepacks', e);
           this.errorMsg.textContent = window.PrepackTranslations?.errorRemove || 'Failed to remove items.';
           this.errorMsg.classList.remove('hidden');
        } finally {
           this.syncLocks.delete('remove-all');
           this.rows.forEach(row => {
             row.style.opacity = '1';
             row.querySelector('.prepack-qty-input').disabled = false;
           });
           this.updateTotals();
        }
      });
    }
  }


  updateTotals() {
    let globalQty = 0;
    let globalPriceCents = 0;
    
    this.rows.forEach(row => {
      const input = row.querySelector('.prepack-qty-input');
      const qty = parseInt(input.value, 10) || 0;
      const basePrice = parseInt(row.dataset.basePrice, 10) || 0;
      
      const rowTotalEl = row.querySelector('.prepack-total-amount');
      const rowTotalCents = qty * basePrice;
      
      if(rowTotalEl) {
        rowTotalEl.innerHTML = this.formatMoney(rowTotalCents);
      }
      
      const btnMinus = row.querySelector('.btn-minus');
      if (qty <= 0) {
        btnMinus.classList.add('disabled');
      } else {
        btnMinus.classList.remove('disabled');
      }
      
      globalQty += qty;
      globalPriceCents += rowTotalCents;
    });
    
    if(this.globalQtyEl) this.globalQtyEl.textContent = globalQty;
    if(this.globalPriceEl) this.globalPriceEl.innerHTML = this.formatMoney(globalPriceCents);
    
    // Hide left footer actions if nothing is selected
    const summaryLeft = this.querySelector('.prepack-summary-left');
    if (summaryLeft) {
      if (globalQty === 0) {
        summaryLeft.style.display = 'none';
      } else {
        summaryLeft.style.display = 'flex';
      }
    }
  }

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    // Replace Shopify's {{amount}} / {{amount_with_comma_separator}} etc.
    return this.moneyFormat
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.'))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, Math.round(cents / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.'))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','))
      .replace(/\{\{\s*amount\s*\}\}/, amount);
  }

  async syncDelta(row, oldQty, newQty) {
    const packId = row.dataset.packId;
    this.syncLocks.add(packId);
    this.errorMsg.classList.add('hidden');
    const notification = this.querySelector('.prepack-added-notification');
    if (notification) notification.classList.add('hidden');
    
    // Unique identifier combining the product.id and the JSON pack config id
    const fullPrepackId = `${this.productId}:${row.dataset.packId}`;
    
    // Calculate the difference. We post to /cart/update.js using delta instead of fully reloading the cart
    // to allow multiple rapid clicks to resolve correctly without the cart being corrupted.
    
    try {
      const distribution = JSON.parse(row.querySelector('.pack-distribution-data').textContent);
      const delta = newQty - oldQty;
      
      if (delta > 0) {
        // We only add the delta
        const items = distribution.map(dist => ({
          id: parseInt(dist.variant_id, 10),
          quantity: parseInt(dist.qty, 10) * delta,
          properties: {
            _prepack_id: fullPrepackId,
            _prepack_name: `${this.productTitle} — ${row.dataset.packName}`,
            _prepack_size: dist.size.toString(),
            _pack_base_qty: dist.qty.toString()
          }
        }));
        
        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ 
            items,
            sections: ['cart-icon-bubble'],
            sections_url: window.location.pathname
          })
        });
        
        const data = await response.json();
        if (data.status && data.message) throw new Error(data.description || data.message);
        
        // Invalidate cart cache so the next removal fetches fresh data
        this.cart = null;
        
        // Show success confirmation
        if (notification) {
          notification.classList.remove('hidden');
        }
        
        // Update bubble natively correctly
        if (data.sections && data.sections['cart-icon-bubble']) {
          const oldBubble = document.getElementById('cart-icon-bubble');
          const tempDiv = new DOMParser().parseFromString(data.sections['cart-icon-bubble'], 'text/html');
          const newBubble = tempDiv.getElementById('cart-icon-bubble') || tempDiv.body.firstElementChild;
          if (oldBubble && newBubble) oldBubble.innerHTML = newBubble.innerHTML;
        }
        
      } else if (delta < 0) {
        // We must fetch cart, find keys, and SET absolute totals.
        if (!this.cart) {
          const res = await fetch(`${window.Shopify.routes.root}cart.js`);
          this.cart = await res.json();
        }
        const cart = this.cart;
        
        const itemsForPrepack = cart.items.filter(item => 
          item.properties && item.properties._prepack_id === fullPrepackId
        );
        
        const updates = {};
        itemsForPrepack.forEach(item => {
          const dist = distribution.find(d => parseInt(d.variant_id) === item.variant_id);
          if (dist) {
            updates[item.key] = parseInt(dist.qty, 10) * newQty;
          } else {
            throw new Error("Variant configuration mismatch. Please clear this item manually or contact support.");
          }
        });
        
        if(Object.keys(updates).length > 0) {
          const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ 
              updates,
              sections: ['cart-icon-bubble'],
              sections_url: window.location.pathname
            })
          });
          const data = await response.json();
          this.cart = data;
          if (data.status && data.message) throw new Error(data.description || data.message);

          // Update bubble natively correctly
          if (data.sections && data.sections['cart-icon-bubble']) {
            const oldBubble = document.getElementById('cart-icon-bubble');
            const tempDiv = new DOMParser().parseFromString(data.sections['cart-icon-bubble'], 'text/html');
            const newBubble = tempDiv.getElementById('cart-icon-bubble') || tempDiv.body.firstElementChild;
            if (oldBubble && newBubble) oldBubble.innerHTML = newBubble.innerHTML;
          }
        }
      }
      
      // Successfully updated cart for this row
      row.dataset.currentCartQty = newQty;
      
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true
      }));
        
    } catch (err) {
      console.error(err);
      this.errorMsg.textContent = window.PrepackTranslations?.soldOut || "Just sold out. Refreshing inventory...";
      this.errorMsg.classList.remove('hidden');
      
      await this._refreshInventory();
      this._recomputeCaps();
      
      this.errorMsg.textContent = err.message || window.PrepackTranslations?.errorCart || "Error updating cart. Inventory refreshed.";
      
      // Revert UI to oldQty
      row.dataset.currentCartQty = oldQty;
      row.querySelector('.prepack-qty-input').value = oldQty;
      this.updateTotals();
      this._recomputeCaps();
    } finally {
      this.syncLocks.delete(packId);
    }
  }
}

customElements.define('prepack-form', PrepackForm);

/* ====================================================
   PREPACK CART SYNC & VIRTUAL PANTRY (Delegated on document)
   ==================================================== 
   This module handles cart interactions without relying on isolated Web Components.
   It intercepts clicks globally to prevent native Dawn JS conflicts.
   
   Virtual Pantry Feature:
   To prevent "Already sold out" errors when users add overlapping packs 
   (same product, different size configurations), this script dynamically:
   1. Clones the base inventory map from the product page or fetches it via JSON API.
   2. Iterates over all active cart items, subtracting their quantities from the virtual inventory.
   3. Recalculates the maximum available packs for each row.
   4. Enforces the strict `max` locally via Math.min() even if the backend `inventory_policy` is 'continue'.
*/


(function initPrepackCartSync() {
  const syncLocks = new Set();
  
  const baseInventoryMaps = {}; // { productId: { variantId: { qty, policy, management } } }
  const multiGroups = [];

  function rescanAndRecomputeCart() {
    const cartGroups = Array.from(document.querySelectorAll('.prepack-cart-group'));
    const groupsByProduct = {};
    cartGroups.forEach(g => {
      const pid = g.dataset.productId;
      if (!groupsByProduct[pid]) groupsByProduct[pid] = [];
      groupsByProduct[pid].push(g);
    });
    
    multiGroups.length = 0;
    Object.values(groupsByProduct).forEach(arr => {
      if (arr.length >= 2) multiGroups.push(arr);
    });
    
    for (const groups of multiGroups) {
      const pid = groups[0].dataset.productId;
      const map = baseInventoryMaps[pid];
      if (!map) continue;
      
      const virtualPantry = JSON.parse(JSON.stringify(map));
      
      // -- Start Dynamic Matrix Solver for Cart --
      const packs = [];
      groups.forEach(row => {
        const mapRaw = row.dataset.updateMap;
        const dist = [];
        if (mapRaw) {
          mapRaw.split(',').forEach(part => {
            if (!part || !part.includes('|')) return;
            const [vid, bq] = part.split('|');
            dist.push({ variant_id: vid, qty: parseInt(bq, 10) });
          });
        }
        const packStockRaw = row.dataset.initialStock || row.dataset.packStock;
        const initial_stock = (!packStockRaw || packStockRaw === 'null') ? 0 : parseInt(packStockRaw, 10);
        packs.push({ row, dist, initial_stock });
      });

      if (packs.length > 0) {
        const initialInv = {};
        packs.forEach(pack => {
          pack.dist.forEach(d => {
            initialInv[d.variant_id] = (initialInv[d.variant_id] || 0) + (pack.initial_stock * d.qty);
          });
        });

        const consumed = {};
        Object.keys(initialInv).forEach(vid => {
          const vLive = map[vid] ? Math.max(0, map[vid].qty) : 0;
          consumed[vid] = Math.max(0, (initialInv[vid] || 0) - vLive);
        });

        const packsSold = solvePacksSold(packs, consumed);
        
        // Validate residual (Risk #4: Least-squares hides inconsistency)
        let inconsistent = false;
        Object.keys(initialInv).forEach(vid => {
          let calculatedConsumption = 0;
          packs.forEach((p, i) => {
            const d = p.dist.find(x => String(x.variant_id) === vid);
            if (d) calculatedConsumption += (d.qty * (packsSold[i] || 0));
          });
          if (calculatedConsumption !== consumed[vid]) {
            inconsistent = true;
          }
        });

        if (inconsistent) {
          console.warn('[Prepack] Cart: Inconsistent variant inventory detected. Falling back to loose variant limits.');
          window.Sentry?.captureMessage('[Prepack] Cart: Inconsistent variant inventory', { level: 'warning' });
          window.dispatchEvent(new CustomEvent('prepack:error', { detail: 'Cart: Inconsistent variant inventory' }));
        } else {
          packs.forEach((pack, i) => {
            const sold = packsSold[i] || 0;
            pack.row.dataset.packStock = Math.max(0, pack.initial_stock - sold);
          });
        }
      }
      // -- End Dynamic Matrix Solver --
      
      const selections = [];
      groups.forEach(row => {
         const qty = parseInt(row.querySelector('.prepack-qty-input').value, 10) || 0;
         const mapRaw = row.dataset.updateMap; // "variantId|baseQty,..."
         const dist = [];
         if (mapRaw) {
           mapRaw.split(',').forEach(part => {
             if (!part || !part.includes('|')) return;
             const [vid, bq] = part.split('|');
             dist.push({ variant_id: vid, qty: parseInt(bq, 10) });
           });
         }
         selections.push({ row, qty, dist });
         
         dist.forEach(d => {
           if (virtualPantry[d.variant_id]) {
              virtualPantry[d.variant_id].qty -= (d.qty * qty);
           }
         });
      });
      
      selections.forEach(({ row, qty, dist }) => {
        const pantryForFill = JSON.parse(JSON.stringify(virtualPantry));
        const availableToAdd = getFillCount(dist, pantryForFill);
        const variantCap = qty + availableToAdd;
        
        const packStockRaw = row.dataset.packStock;
        const packStock = (!packStockRaw || packStockRaw === 'null') ? Infinity : parseInt(packStockRaw, 10);
        
        const absoluteMax = Math.min(packStock, variantCap);
        
        row.dataset.maxPacks = absoluteMax;
        const input = row.querySelector('.prepack-qty-input');
        if (input) input.setAttribute('max', absoluteMax);
        
        const btnPlus = row.querySelector('.prepack-btn-plus');
        if (btnPlus) btnPlus.disabled = (qty >= absoluteMax);
      });
    }
  }

  async function initCartVirtualPantry() {
    rescanAndRecomputeCart();
    const fetchPromises = [];
    
    for (const groups of multiGroups) {
      const pid = groups[0].dataset.productId;
      if (baseInventoryMaps[pid]) continue;
      
      const href = groups[0].querySelector('.cart-item__name')?.href;
      if (!href) continue;
      const url = new URL(href, window.location.origin);
      const handle = url.pathname.split('/products/')[1]?.split('?')[0];
      if (!handle) continue;
      
      const nocacheUrl = `${url.pathname}?t=${Date.now()}`;
      fetchPromises.push(
        fetchWithRetry(nocacheUrl, { cache: 'no-store' })
          .then(res => res.text())
          .then(text => {
            const parser = new DOMParser();
            const doc = parser.parseFromString(text, 'text/html');
            const mapScript = doc.querySelector(`#prepack-inventory-map-${pid}`);
            
            if (!mapScript) {
              console.error(`[Prepack] CRITICAL FAILURE: Could not find script tag #prepack-inventory-map-${pid} for Cart Sync. Virtual pantry will be stale.`);
              window.Sentry?.captureMessage('[Prepack] Cart refresh selector missed', { level: 'error' });
              window.dispatchEvent(new CustomEvent('prepack:error', { detail: 'Cart refresh selector missed' }));
              return;
            }
            
            baseInventoryMaps[pid] = JSON.parse(mapScript.textContent);
          })
          .catch(e => {
            console.error('[Prepack] Failed to init virtual pantry for', url.pathname, e);
            window.Sentry?.captureMessage('[Prepack] Failed to init virtual pantry', { level: 'error' });
          })
      );
    }
    
    if (fetchPromises.length > 0) {
      await Promise.all(fetchPromises);
      rescanAndRecomputeCart();
    }
  }

  // Kickoff on load
  initCartVirtualPantry();

  document.addEventListener('click', async (e) => {
    const btnMinus = e.target.closest('.prepack-btn-minus');
    const btnPlus = e.target.closest('.prepack-btn-plus');
    const removeBtn = e.target.closest('.prepack-remove-btn');



    let row = null;

    if (btnMinus) { row = btnMinus.closest('.prepack-cart-group'); }
    else if (btnPlus) { row = btnPlus.closest('.prepack-cart-group'); }
    else if (removeBtn) { row = removeBtn.closest('.prepack-cart-group'); }

    if (!row) return; // Not a prepack click
    


    // Actually the prepack cart row element contains the data attrs
    const dataRow = row;
    const lockId = dataRow.dataset.index || dataRow.dataset.packId || 'global';

    e.preventDefault();
    e.stopImmediatePropagation();

    if (syncLocks.has(lockId)) {

      return;
    }

    const currentQtyStr = dataRow.dataset.currentQty;
    const currentQty = parseInt(currentQtyStr, 10) || 0;
    
    // Read max cap either from JS recompute or liquid initial render
    let max = parseInt(dataRow.dataset.maxPacks, 10);
    if (isNaN(max)) {
      const input = dataRow.querySelector('.prepack-qty-input');
      if (input) max = parseInt(input.getAttribute('max'), 10);
    }
    if (isNaN(max)) max = 9999;
    
    let newVal = currentQty;



    if (btnMinus) newVal = Math.max(0, currentQty - 1);
    if (btnPlus) newVal = Math.min(currentQty + 1, max);
    if (removeBtn) newVal = 0;



    if (isNaN(newVal) || newVal < 0) newVal = 0;
    if (newVal > max) newVal = max;
    
    
    // TEMPORARILY bypass early return for debugging if remove button is pressed!
    if (newVal === currentQty && !removeBtn) {

      return;
    }
    


    // Fetch native cart items wrapper and disable UI
    // We do this to prevent multiple rapid clicks corrupting the section HTML swap
    syncLocks.add(lockId);
    const input = dataRow.querySelector('.prepack-qty-input');
    if (input) input.disabled = true;
    if (btnMinus) btnMinus.disabled = true;
    if (btnPlus) btnPlus.disabled = true;
    if (dataRow) dataRow.style.opacity = '0.5';

    // Parse distribution Map
    const updates = {};
    const mapRaw = dataRow.dataset.updateMap;
    if (mapRaw) {
      const parts = mapRaw.split(',');
      parts.forEach(part => {
        if (!part || !part.includes('|')) return;
        const [key, baseQtyStr] = part.split('|');
        const baseQty = parseInt(baseQtyStr || 1, 10);
        updates[key] = baseQty * newVal;
      });
    }

    try {
      if (Object.keys(updates).length > 0) {
        const cartItems = document.getElementById('main-cart-items');
        if (cartItems) cartItems.classList.add('cart__items--disabled');

        const sectionsToRender = [
          {
            id: 'main-cart-items',
            section: document.getElementById('main-cart-items').dataset.id,
            selector: '.cart-items-contents'
          },
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '.shopify-section'
          },
          {
            id: 'main-cart-footer',
            section: document.getElementById('main-cart-footer').dataset.id,
            selector: '.cart-totals-contents'
          }
        ];

        const body = {
          updates,
          sections: sectionsToRender.map((s) => s.section),
          sections_url: window.location.pathname
        };

        const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(body)
        });
        
        const parsedState = await response.json();
        
        if (!response.ok || parsedState.status) {
           throw new Error(parsedState.description || 'Update failed on server');
        }
        
        // 2. Patch DOM
        sectionsToRender.forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
            
          const html = parsedState.sections[section.section];
          if (!html) return;
          
          const tempDiv = new DOMParser().parseFromString(html, 'text/html');
          const element = tempDiv.querySelector(section.selector) || tempDiv.body.firstElementChild;
          
          if (elementToReplace && element) {
            elementToReplace.innerHTML = element.innerHTML;
          }
        });

        // 3. Handle empty cart state
        const cartItemsEl = document.querySelector('cart-items');
        const mainCartFooter = document.getElementById('main-cart-footer');
        const isEmpty = parsedState.item_count === 0;
        
        if (cartItemsEl) cartItemsEl.classList.toggle('is-empty', isEmpty);
        if (mainCartFooter) mainCartFooter.classList.toggle('is-empty', isEmpty);

        // 4. Publish cartUpdate
        try {
          if (window.publish && window.PUB_SUB_EVENTS) {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'prepack-cart-row', cartData: parsedState });
          }
        } catch(e) { console.warn('Could not publish cartUpdate event', e); }

        // Exit before error-recovery UI unlock
        return;
      }
    } catch (err) {
      console.error(err);
      
      // Inline error message for the row to match Dawn style if possible
      const lineItemError = document.getElementById(`Line-item-error-${dataRow.dataset.index}`);
      if (lineItemError) {
         lineItemError.querySelector('.cart-item__error-text').innerHTML = err.message;
         lineItemError.style.display = 'block';
      }

      if (err.message && err.message.toLowerCase().includes('sold out')) {
         const pid = dataRow.dataset.productId;
         const href = dataRow.querySelector('.cart-item__name')?.href;
         if (href) {
           const url = new URL(href, window.location.origin);
           const handle = url.pathname.split('/products/')[1]?.split('?')[0];
           if (handle) {
             if (lineItemError) lineItemError.querySelector('.cart-item__error-text').innerHTML = window.PrepackTranslations?.soldOut || "Just sold out. Refreshing inventory...";
             fetch(`/products/${handle}.js`).then(res => res.json()).then(product => {
                const map = {};
                product.variants.forEach(v => {
                   map[v.id] = { qty: v.inventory_quantity, policy: v.inventory_policy, management: v.inventory_management };
                });
                baseInventoryMaps[pid] = map;
                rescanAndRecomputeCart();
                if (lineItemError) lineItemError.querySelector('.cart-item__error-text').innerHTML = err.message || window.PrepackTranslations?.errorCart || "Error updating cart. Inventory refreshed.";
             }).catch(e => console.error(e));
           }
         }
      }

      // Revert UI if error ONLY
      if (input) input.disabled = false;
      if (btnMinus) btnMinus.disabled = false;
      if (btnPlus) btnPlus.disabled = false;
      if (dataRow) dataRow.style.opacity = '1';
      rescanAndRecomputeCart();
    } finally {
      syncLocks.delete(lockId);
      const cartItems = document.getElementById('main-cart-items');
      if(cartItems) cartItems.classList.remove('cart__items--disabled');
    }
  }, true); // Use document capture phase
})();
